import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Container, Stack } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form5227ShcE = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Schedule E Instructions for Form 5227 – Income & Loss Guide"
          description="Learn how to complete Schedule E for Form 5227 with clear steps for reporting passive, non-passive income, losses, and required details for estates and trusts."
          keywords=""
          canonical="resources/tax-exempt/form-5227-schedule-e/"
          ogType="article"
          jsonLdPricing=""
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight"> Schedule E</span>
          </span>{' '}
          (Form 1040) for Form 5227
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Schedule E with Form 5227 quickly and stay IRS-compliant!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5227sche`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-5227-schedule-e/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Schedule E (Form 1040) at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Schedule E?
                        </strong>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Schedule E (Form 1040), Supplemental Income and Loss
                        </strong>
                        , is used by certain{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 5227 filers
                        </strong>{' '}
                        to report income or losses from{' '}
                        <strong style={{ fontWeight: 600 }}>
                          rents, royalties, partnerships, S corporations,
                          estates and trusts, and REMICs
                        </strong>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Uses it with Form 5227?
                        </strong>{' '}
                        A split-interest trust that receives applicable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          rental, royalty, partnership, S corporation, estate or
                          trust, or REMIC income
                        </strong>{' '}
                        may need to use Schedule E to calculate the amount
                        reported on{' '}
                        <Link
                          href="/tax-exempt/e-file-irs-form-5227/"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS Form 5227
                        </Link>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule E reports the applicable{' '}
                        <strong style={{ fontWeight: 600 }}>
                          supplemental income or loss
                        </strong>
                        , which is then carried to the corresponding line of{' '}
                        <Link
                          href="/resources/tax-exempt/501c3-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 5227
                        </Link>
                        .
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 5227 Connection:
                        </strong>{' '}
                        Schedule E is one of the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          supporting schedules used with Form 5227
                        </strong>{' '}
                        when the trust has the applicable types of income or
                        losses.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why Trusts Filing Form 5227 Need Schedule E (1040)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who Needs to File Schedule E: The Trust or the
                          Beneficiary?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What happens when a trust filing Form 5227 forgets to
                          add Schedule E?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What penalties apply for filing an incomplete or
                          incorrect Form 5227, including missing Schedule E
                          (1040)?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to File Schedule E (1040) with TaxZerone
                        </Link>
                        <List
                          sx={{
                            mb: 0,

                            borderRadius: 2,
                          }}
                        >
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part1" sx={{ color: '#0202ff' }}>
                                Part I: Income or Loss from Rental Real Estate
                                and Royalties
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part2" sx={{ color: '#0202ff' }}>
                                Part II: Income or Loss from Partnerships and S
                                Corporations
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#part3" sx={{ color: '#0202ff' }}>
                                Part III: Income or Loss from Estates and Trusts
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#partiv" sx={{ color: '#0202ff' }}>
                                Part IV: Income or Loss from Real Estate
                                Mortgage Investment Conduits (REMICs)-Residual
                                Holder
                              </Link>
                            </ListItemText>
                          </ListItem>
                          <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#sum" sx={{ color: '#0202ff' }}>
                                Part V: Summary
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>

                    <ListItem
                      sx={{ py: 0.5, alignItems: 'flex-start', mt: -2 }}
                    >
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#add" sx={{ color: '#0202ff' }}>
                          Additional IRS References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="why"
                >
                  Why Trusts Filing Form 5227 Need Schedule E (1040)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even though{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule E (Form 1040)
                  </span>{' '}
                  is commonly associated with individual returns, the IRS also
                  requires{' '}
                  <Link
                    href="/resources/tax-exempt/form-5227-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 5227
                  </Link>{' '}
                  filers to use Schedule E when a trust receives certain types
                  of supplemental income, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Rental real estate income</li>
                  <li>Royalties</li>
                  <li>Partnership income</li>
                  <li>S corporation income </li>
                  <li>Unrelated business taxable income (UBTI) </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Including Schedule E gives the IRS a clear view of how the
                  trust earned its{' '}
                  <span style={{ fontWeight: 600 }}> supplemental income</span>{' '}
                  and helps determine how that income should be taxed or applied
                  to other sections of the return.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who Needs to File Schedule E: The Trust or the Beneficiary?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A <span style={{ fontWeight: 600 }}> trust</span> is
                  responsible for{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form 5227
                  </Link>
                  , and it must also attach
                  <span style={{ fontWeight: 600 }}> Schedule E</span> whenever
                  it earns any type of supplemental income, such as rental
                  income, royalties, or partnership and S corporation income.{' '}
                  <span style={{ fontWeight: 600 }}>Beneficiaries,</span>{' '}
                  however, do <span style={{ fontWeight: 600 }}>not</span>{' '}
                  attach Schedule E to the 5227 form.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Beneficiaries instead file{' '}
                  <span style={{ fontWeight: 600 }}>Form 1040,</span> not Form
                  5227. They are required to include{' '}
                  <span style={{ fontWeight: 600 }}>Schedule E</span> with their
                  Form 1040 only when they receive income reported on a{' '}
                  <span style={{ fontWeight: 600 }}>Schedule K-1</span> from a
                  trust, estate, partnership, or S corporation.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="happen"
                >
                  What happens when a trust filing Form 5227 forgets to add
                  Schedule E?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If a trust filing{' '}
                  <Link
                    href="/resources/tax-exempt/form-5227-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 5227
                  </Link>{' '}
                  fails to attach Schedule E even though it earned income that
                  must be reported on that schedule, the IRS treats the return
                  as incomplete.{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Penalties may apply
                  </span>{' '}
                  if the missing information results in inaccurate reporting or
                  affects any tax calculations, including unrelated business
                  taxable income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="what"
                >
                  What penalties apply for filing an incomplete or incorrect
                  Form 5227, including missing Schedule E (1040)?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS imposes daily penalties when a trust submits a Form
                  5227 that is not{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    complete and accurate,
                  </span>{' '}
                  which includes leaving out schedules such as Schedule E. The
                  penalty structure is as follows:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> $25 per day,</span> up to
                    a{' '}
                    <span style={{ fontWeight: 600 }}>maximum of $13,000,</span>{' '}
                    <br />
                    for trusts with gross income{' '}
                    <span style={{ fontWeight: 600 }}>
                      below the IRS threshold.
                    </span>
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> $125 per day,</span> up
                    to a{' '}
                    <span style={{ fontWeight: 600 }}>maximum of $65,000,</span>{' '}
                    <br />
                    for trusts with gross income{' '}
                    <span style={{ fontWeight: 600 }}>over $327,000</span>{' '}
                    (current IRS threshold).{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The higher penalty level applies only when the trust’s gross
                  income exceeds{' '}
                  <span style={{ fontWeight: 600 }}>$327,000,</span> according
                  to the most recent IRS instructions.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> A trust can avoid filing Schedule E (1040) only
                  if it did not receive any Schedule-E-type income such as
                  rentals, royalties, partnerships, S-corps, estates, trusts, or
                  REMIC income.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to File Schedule E (1040) with TaxZerone
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="part1"
                >
                  Part I: Income or Loss from Rental Real Estate and Royalties
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-income-loss-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-income-loss-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-income-loss-img.png"
                    loading="lazy"
                    alt="Part I: Income or Loss from Rental Real Estate and Royalties"
                    width={760}
                    height={396}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section is where the trust reports rental or royalty
                  income that must also be carried into{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-5227/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS Form 5227
                  </Link>
                  .
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Use this part only if the trust earned rental or
                  royalty income; use Schedule C only if it is running a rental
                  business.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line A:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the trust paid anyone{' '}
                  <span style={{ fontWeight: 600 }}> $600+</span> for
                  rental-related services, it must answer whether{' '}
                  <span style={{ fontWeight: 600 }}>Form 1099</span> is
                  required.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>“Yes”:</span> if the trust
                    made those kinds of payments.{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>“No”:</span> if it
                    didn’t.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line B:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the trust was required to file Form 1099, this asks whether
                  those 1099 forms were already filed or will be filed.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>“Yes”:</span> if the trust
                    has filed or will file the 1099 forms.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>“No”:</span> only if it
                    failed to file them
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 1a:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    full physical address
                  </span>{' '}
                  of each rental property.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    Add the{' '}
                    <span style={{ fontWeight: 600 }}>
                      street address, city, state, and ZIP code.
                    </span>
                  </li>
                  <li>
                    {' '}
                    If it’s outside the U.S., list the{' '}
                    <span style={{ fontWeight: 600 }}>
                      city, province/state, country, and postal code
                    </span>{' '}
                    instead.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 1b:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here you’ll enter a{' '}
                  <span style={{ fontWeight: 600 }}>single code</span> that
                  tells the IRS what kind of property you’re renting:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>5 – Land rental</span>
                    <br />
                    Use this if you’re renting out land only with no buildings
                    on it.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      7 – Self-rental
                    </span>{' '}
                    <br />
                    Use this when you rent the property to a business you
                    actively participate in.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>8 – Other</span> <br />
                    Use this if your property doesn’t match any of the other
                    categories.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 2:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>Fair Rental Days – </span>
                    Days the property was rented at a normal, fair market price.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Personal Use Days –
                    </span>{' '}
                    Days you, a co-owner, family members, or anyone paying less
                    than fair rent used the property.
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Qualified Joint Venture (QJV) -
                    </span>{' '}
                    For IRS 5227 filers, leave this box unchecked.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any personal-use days don’t count as rental days, so be sure
                  to split the expenses based on how the property was actually
                  used.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Not personal use:</span>{' '}
                  Days spent working full-time on repairs or days it was your
                  main home before or after renting (as long as you tried to
                  rent it for 12 months).
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> <br />
                  <ul>
                    {' '}
                    <li style={{ marginBottom: 8 }}>
                      Even if the trust used the property as a home or not, any
                      days of personal use can’t be treated as rental days.
                    </li>
                    <li>
                      You must split (allocate) your expenses based on how many
                      days the property was used personally vs. Rented.{' '}
                    </li>
                  </ul>
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Income:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-income-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-income-img.png"
                    loading="lazy"
                    alt="Schedule E (Form 1040) Part I: Income reported on lines 3 - 4"
                    width={760}
                    height={66}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 3:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total rent the trust received
                  </span>{' '}
                  for each property in Columns A, B, or C. This includes:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li> Normal rent payments</li>
                  <li> Rent for a room/space inside a property</li>
                  <li>
                    {' '}
                    Non-cash rent (services or property) — report the fair
                    market value
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Notes:</b> <br />
                  <ul style={{ marginRight: 24 }}>
                    {' '}
                    <li style={{ marginBottom: 8 }}>
                      If the trust provided hotel-style services (like regular
                      cleaning or maid service), the activity belongs on
                      Schedule C, not Schedule E.
                    </li>
                    <li>
                      If the trust holds real estate for sale (as a dealer),
                      that also goes on Schedule C, not Schedule E.
                    </li>
                  </ul>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 4:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line only if the trust received royalties, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Oil, gas, or mineral royalties </li>
                  <li> Copyright or patent royalties </li>
                  <li> Name, image, and likeness (NIL) royalties</li>
                  <li>Licensing or merchandising payments </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report royalties separately for each property (A, B, or C).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the trust received{' '}
                  <span style={{ fontWeight: 600 }}> $10 or more,</span> a Form
                  1099-MISC should be issued.
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If royalty&apos;s income is tied to a business
                  activity, it belongs on Schedule C instead. But if it’s pure
                  royalty’s income, the trust reports it here on Schedule E.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Expenses:
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-expenses-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-expenses-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-expenses-img.png"
                    loading="lazy"
                    alt="Schedule E (Form 1040) Part I: Expenses reported on lines 5- 26"
                    width={760}
                    height={332}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 5 - Advertising
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any costs the trust paid to advertise the rental
                  property— online listings, flyers, signs, platform fees, etc.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Other Expense Rules
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can deduct ordinary and necessary rental expenses for each
                  property (taxes, repairs, insurance, management fees,
                  commissions, depreciation, etc.).
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Do NOT deduct:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>The value of your own labor </li>
                  <li>
                    Costs for major improvements (these must be
                    capitalized){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If only part of a home is rented, deduct only the portion that
                  applies to the rental space.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Accessibility Costs:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The trust may be able to claim:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    A <span style={{ fontWeight: 600 }}> tax credit</span> (Form
                    8826) for accessibility improvements,{' '}
                  </li>
                  <li>
                    A <span style={{ fontWeight: 600 }}>deduction</span> of up
                    to $15,000 for removing barriers for disabled or elderly
                    individuals.
                    <br />{' '}
                    <span style={{ fontWeight: 600 }}>
                      But not for the same cost.
                    </span>
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 6 - Auto and Travel
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can deduct the ordinary and necessary vehicle and travel
                  costs connected to your rental activity. This includes{' '}
                  <span style={{ fontWeight: 600 }}> 50% of meals</span> while
                  traveling away from home.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Important: If you use the standard mileage rate, you cannot
                  deduct:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Actual vehicle expenses </li>
                  <li>Lease/rental payments </li>
                  <li>Depreciation </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  You have two ways to deduct auto expenses:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  1. Standard Mileage Rate (70 ¢ per mile for 2025)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    You owned the vehicle and used the standard mileage rate the
                    first year it was placed in service,{' '}
                  </li>
                  <li>
                    You leased the vehicle and used the mileage rate for the
                    entire lease period.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you use standard mileage:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Multiply your rental-related miles Ã— 70 ¢ </li>
                  <li>Add parking fees & tolls </li>
                  <li>
                    Enter the total on{' '}
                    <span style={{ fontWeight: 600 }}> Line 6</span>{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  2. Actual Expenses Method
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    Include the rental-use portion of{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      gas, oil, repairs, insurance, tires, tags, etc.
                    </span>{' '}
                    on Line 6{' '}
                  </li>
                  <li>
                    Put{' '}
                    <span style={{ fontWeight: 600 }}>
                      lease or rental payments
                    </span>{' '}
                    on <span style={{ fontWeight: 600 }}>Line 19</span>{' '}
                  </li>
                  <li>
                    Put <span style={{ fontWeight: 600 }}>depreciation</span> on{' '}
                    <span style={{ fontWeight: 600 }}>Line 18</span>
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you claim any auto expenses (either method),
                  you must complete Part V of Form 4562 and attach the form to
                  your return.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 7 - Cleaning and Maintenance
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Costs for cleaning services, landscaping, pest control, snow
                  removal, or general upkeep.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 8 - Commissions
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Real estate agent commissions paid for finding tenants or
                  managing rentals.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 9 - Insurance
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Premiums for property insurance, liability insurance, flood
                  insurance, etc.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 10 - Legal and Professional Fees
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    Tax advice related to its rental property or royalty
                    income{' '}
                  </li>
                  <li>
                    Preparation of tax forms connected to that rental or royalty
                    activity{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Do NOT deduct on Line 10:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Defending or protecting ownership/title </li>
                  <li>Recovering the property </li>
                  <li>Improving or developing the property </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These costs must be capitalized (added to the property’s
                  basis), not deducted as expenses.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 11 - Management Fees
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Payments to property management companies or rental platforms
                  that charge management fees.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 12 - Mortgage Interest (Paid to Banks/Lenders)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report the interest the trust paid on a rental-property
                  mortgage{' '}
                  <span style={{ fontWeight: 600 }}>
                    to a bank or financial institution.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This amount usually appears on{' '}
                  <span style={{ fontWeight: 600 }}>Form 1098</span> if you pay
                  $600 or more.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#c026521f',
                    borderLeft: '5px solid #c02652',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                    Important rules:
                  </Typography>
                  <Box component="ul" sx={{ mb: 1, '& li ': { mb: 1.2 } }}>
                    <li>
                      Only deduct interest for the portion of the loan actually
                      used for the rental activity.{' '}
                    </li>
                    <li>
                      Prepaid interest (points) must be spread over the life of
                      the loan—not deducted at once.
                    </li>
                    <li>
                      If the 1098 doesn’t match the trust’s actual interest
                      paid, you can use the correct amount but attach a
                      statement explaining why.{' '}
                    </li>
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 13 - Other Interest
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    The lender was not a financial institution (no Form 1098),
                  </li>
                  <li>
                    Someone else received the Form 1098 but the trust paid part
                    of the interest (report the trust’s share here with an
                    attached statement).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  How to Allocate Interest
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  The IRS requires{' '}
                  <span style={{ fontWeight: 600 }}> “tracing.”</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This means you deduct interest{' '}
                  <span style={{ fontWeight: 600 }}>
                    based on how the loan money was actually used.
                  </span>{' '}
                  (Example: if 70% of the loan is funded by the rental activity,
                  then 70% of the interest is deductible.)
                </Typography>
                <Alert
                  severity="info"
                  icon={<TipsAndUpdatesIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                >
                  <b>Tips:</b> Keeping each loan’s funds separate makes tracing
                  much easier.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Business Interest Limitation
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This applies only if the trust of rental activity rises to the
                  level of a trade or business (rare Form 5227 filers).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If it does, you may need{' '}
                  <span style={{ fontWeight: 600 }}> Form 8990</span> to compute
                  allowed interest.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  If not, simply report eligible interest on Lines 12 and 13.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 14 - Repairs
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this line to deduct the trust’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    repair and maintenance costs
                  </span>
                  —the expenses that keep the rental property in good working
                  condition.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Repairs (Deductible)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These do not improve or upgrade the property; they simply keep
                  things running.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Examples:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Fixing a broken lock </li>
                  <li>Patching drywall </li>
                  <li>Painting a room </li>
                  <li>Minor plumbing fixes </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These can be deducted in full on Line 14.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Improvements (NOT deductible here)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the trust upgrades, restores, or changes how the property
                  is used, that is an improvement, not a repair.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Examples:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Replacing an entire HVAC system </li>
                  <li>Adding new insulation </li>
                  <li>Installing new windows </li>
                  <li>Remodeling a room </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  These must be capitalized and depreciated over time (reported
                  on <span style={{ fontWeight: 600 }}> Line 18</span>).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 15 - Supplies
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Small items used for rental operations—light bulbs, cleaning
                  supplies, small tools, etc.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 16 - Taxes
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Property taxes, local occupancy taxes, or other government
                  charges on the property.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 17 - Utilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to deduct utility costs the trust paid for the
                  rental property—such as electricity, water, gas, trash
                  service, or internet if it relates to the rental.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can also deduct rental-related phone calls, like calling a
                  tenant or service provider.
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 17, md: 16, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  But NOT deductible:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The base cost of the first phone line in a residence (this is
                  always considered personal, even if sometimes used for rental
                  calls).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 18 - Depreciation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Depreciation lets the trust recover the cost of a rental
                  building over time. Since buildings wear out, part of their
                  cost is deducted each year—
                  <span style={{ fontWeight: 600 }}>
                    but land is never depreciated.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Separating cost of land and buildings{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    Depreciation starts when the rental property is{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      ready to be rented.
                    </span>{' '}
                  </li>
                  <li>
                    It continues until the trust has fully deducted its cost or
                    stopped using the property for rental income.{' '}
                  </li>
                  <li>
                    If the property was purchased with land included, the trust
                    must{' '}
                    <span style={{ fontWeight: 600 }}>separate the cost</span>{' '}
                    between:{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Land (not depreciable)
                    </span>{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Building (depreciable){' '}
                    </span>
                    <br />
                    Usually based on fair market value or property tax
                    assessments.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  When 5227 filers must attach Form 4562:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Attach <span style={{ fontWeight: 600 }}> Form 4562</span>{' '}
                  only if the trust is claiming:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    Depreciation for property{' '}
                    <span style={{ fontWeight: 600 }}>
                      first placed in service in 2025,
                    </span>{' '}
                    or
                  </li>
                  <li>
                    Depreciation for{' '}
                    <span style={{ fontWeight: 600 }}>listed property</span>{' '}
                    (like a vehicle), or{' '}
                  </li>
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      Section 179 deduction
                    </span>{' '}
                    or amortization that began in 2025{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the trust owns mineral property (oil, gas,
                  minerals), it may claim a depletion of deduction instead.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 19 - Other Expenses
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    List any rental or royalty expenses that don’t fit on Lines
                    5–18 (like bank fees, supplies, software, HOA fees,
                    etc.).{' '}
                  </li>
                  <li>
                    {' '}
                    If applicable, you can also include certain energy-efficient
                    building deductions under section 179D (Form 7205).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 20 - Total Expenses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add all the above expenses (lines 5–19) to get your total
                  deductions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 21 - Your Profit or Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 21 basically shows whether you{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    made money or lost money
                  </span>{' '}
                  from your rental or royalty activity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Line 21 = Income – Expenses{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If Line 21 shows a loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Sometimes you’re{' '}
                  <span style={{ fontWeight: 600 }}> not allowed</span> to
                  deduct the full loss right away.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  In that case, the IRS makes you check your loss using{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 6198 (At-Risk Rules)
                  </span>
                  .
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>Write “Form 6198” next to Line 21 </li>
                  <li>Attach the form </li>
                  <li>Only enter the part of the loss that’s allowed </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 22 - Rental Real Estate Loss You Can Actually Deduct
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  (Only for rental real estate -
                  <span style={{ fontWeight: 600 }}> not</span> for royalty
                  income)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 21 has a{' '}
                  <span style={{ fontWeight: 600 }}>rental loss,</span> the IRS
                  may limit it under the{' '}
                  <span style={{ fontWeight: 600 }}>
                    passive activity loss rules
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  If your rental is passive
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    You may need{' '}
                    <span style={{ fontWeight: 600 }}> Form 8582</span>.{' '}
                  </li>
                  <li>
                    That form tells you{' '}
                    <span style={{ fontWeight: 600 }}>
                      how much of the loss you’re allowed to claim this year
                    </span>
                    .{' '}
                  </li>
                  <li>Enter that allowed amount on Line 22. </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  If your rental is NOT passive
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>you qualify for a rental real estate exception</li>
                  <li>Then no Form 8582 is needed</li>
                  <li>
                    Just bring the loss from Line 21 straight down to Line
                    22.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  If you had losses from a prior year
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    If you have rental losses from a previous year and Form 8582
                    now allows you to use them, just add those losses to Line 22
                    as well.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Lines 23a-23e: Totals
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>23a: </span> Add up all
                    rental income amounts from{' '}
                    <span style={{ fontWeight: 600 }}>Line 3</span> (for every
                    rental property) and enter the total here.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>23b: </span> Add up all
                    royalty income amounts from{' '}
                    <span style={{ fontWeight: 600 }}>Line 4</span> and enter
                    the total.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>23c: </span> Add up all
                    mortgage interest reported on{' '}
                    <span style={{ fontWeight: 600 }}>Line 12</span> for all
                    properties.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>23d: </span> Add up all
                    depreciation/depletion amounts from{' '}
                    <span style={{ fontWeight: 600 }}>Line 18</span>.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>23e: </span> Add up all
                    total expenses from{' '}
                    <span style={{ fontWeight: 600 }}>Line 20</span> for all
                    properties.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This section simply pulls together all your column totals in
                  one place for the trust’s final rental/royalty summary.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 24 - Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add up all positive amounts from Line 21 (rental and royalty
                  income).
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Don’t include any losses here — only the income.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 25 - Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add together:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    Any <span style={{ fontWeight: 600 }}> royalty losses</span>{' '}
                    from Line 21, and{' '}
                  </li>
                  <li>
                    Any{' '}
                    <span style={{ fontWeight: 600 }}>
                      rental real estate losses
                    </span>{' '}
                    from Line 22.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the <span style={{ fontWeight: 600 }}>total loss</span>{' '}
                  on Line 25.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 26 - Total Rental & Royalty Income (or Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Combine:
                </Typography>
                <Box
                  component="ul"
                  sx={{ mb: 2, '& li ': { mb: 1.2, fontWeight: 600 } }}
                >
                  <li>Line 24 (income)</li>
                  <li>Line 25 (losses)</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This gives the trust’s{' '}
                  <span style={{ fontWeight: 600 }}>
                    overall net rental/royalty result
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For <span style={{ fontWeight: 600 }}>Form 5227 filers,</span>{' '}
                  this amount flows into the trust income section on later pages
                  (not to Form 1040 — that part of the IRS text applies to
                  individual filers, not trusts).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                    scrollMarginTop: 88,
                  }}
                  id="part2"
                >
                  Part II: Income or Loss from Partnerships and S Corporations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-partnership-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-partnership-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-partnership-img.png"
                    loading="lazy"
                    alt="Part II: Income or Loss from Partnerships and S Corporations"
                    width={760}
                    height={397}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 27
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> “Yes”</span> on Line 27,
                  the IRS wants to make sure the amounts you report{' '}
                  <span style={{ fontWeight: 600 }}>
                    match your Schedule K-1
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> &quot;No&quot; </span> they
                  may send you a notice. Below are the situations you may need
                  to report separately on{' '}
                  <span style={{ fontWeight: 600 }}>Line 28</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Prior-Year Losses Allowed This Year (Basis / At-Risk Rules)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you had losses from past years that were not allowed
                  because of basis or at-risk limits, and they are now
                  deductible:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    Report them on a separate line in column (i) of Line
                    28.{' '}
                  </li>
                  <li>Do not mix these losses with current-year amounts. </li>
                  <li>
                    Write “PYA” in column (a). (PYA = Prior Year
                    Adjustment){' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Prior-Year Passive Losses Not Reported on Form 8582
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you had passive activity losses from earlier years that are
                  now deductible (for example, because you had overall passive
                  income or fully disposed of the activity):
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    Report them on a separate line in column (g) of Line 28.
                  </li>
                  <li>Do not combine with current-year items. </li>
                  <li>Enter “PYA” in column (a).</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Unreimbursed Partnership Expenses (UPE)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These are expenses you paid out of pocket for the partnership,
                  and you were required to pay them under the partnership
                  agreement.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Rules:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>
                    UPE from nonpassive activities → report in column (i) on a
                    separate line.
                  </li>
                  <li>UPE from passive activities </li>
                  <ul>
                    <li>
                      If you must file Form 8582 → do NOT report
                      separately.{' '}
                    </li>
                    <li>
                      If you do not need Form 8582 → report in column (g) on a
                      separate line.{' '}
                    </li>
                  </ul>
                  <li>Write “UPE” in column (a). </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Gambling Income & Losses from Partnerships
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li ': { mb: 1.2 } }}>
                  <li>If the partnership is in the gambling business: </li>
                  <li>Report gambling winnings on Schedule E, column (k). </li>
                  <li>Report gambling losses on Schedule E, column (i). </li>
                  <li>
                    You can only deduct gambling losses up to your total
                    gambling winnings from all sources on your tax return
                    (Schedules 1, C, and E).{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the partnership isn’t in the gambling
                  business, report on gambling winnings on Schedule 1 (Form
                  1040). Gambling losses can be deducted only up to your
                  winnings, and those are reported on Schedule A (Form 1040).
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 28
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Line 28 to list your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    current-year income or loss
                  </span>{' '}
                  from partnerships or S corporations. Put each amount in the{' '}
                  <span style={{ fontWeight: 600 }}>right column</span> (passive
                  or nonpassive). If your K-1 shows extra items, list each one
                  on a <span style={{ fontWeight: 600 }}>separate line</span>{' '}
                  and label it in column (a).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  If You Used a Loan to Invest
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Interest from a loan used to buy or invest must be reported
                  based on how it’s used:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li ': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Business interest:</span>{' '}
                    Schedule E{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Passive interest:</span>{' '}
                    Form 8582, then Schedule E{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Investment interest:
                    </span>{' '}
                    Form 4952{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Personal interest:</span>{' '}
                    Not deductible{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> (a) Name:</span> Enter the
                  name of the partnership or S corporation shown on your K-1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> (b) P or S:</span> Mark P
                  for a partnership or S for an S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (c) Foreign Partnership:
                  </span>{' '}
                  Check this only if the partnership is foreign (formed outside
                  the U.S.).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> (d) EIN:</span> Enter the
                  entity’s Employer Identification Number from the K-1.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (e) Basis Computation Required:
                  </span>{' '}
                  Check out this box if you’re an S corporation owner and must
                  attach a basis worksheet (for losses, distributions, stock
                  sales, or loan repayments).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (f) Any Amount Not at Risk:
                  </span>{' '}
                  Check this if part of your investment is not fully at risk,
                  meaning some losses may not be deductible.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Passive Columns for 5227 Filers{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (g) Passive Loss Allowed:
                  </span>{' '}
                  Enter the passive losses you’re allowed to deduct this year.
                  Use Form 8582 first if required.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> (h) Passive Income:</span>{' '}
                  Enter passive income on the K-1 reports to you.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Nonpassive Columns for 5227 Filers
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}> (i) Nonpassive Loss:</span>{' '}
                  Enter nonpassive losses you can deduct (usually when you
                  actively participate).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (j) Section 179 Deduction:
                  </span>{' '}
                  Enter any Section 179 expenses passed through to you.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    (k) Nonpassive Income:{' '}
                  </span>{' '}
                  Enter nonpassive income you received (from activities you
                  materially participated in).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 29a - Totals (Passive Income Columns)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add up all the amounts in{' '}
                  <span style={{ fontWeight: 600 }}> column (h)</span> (passive
                  income) and{' '}
                  <span style={{ fontWeight: 600 }}>column (k)</span>{' '}
                  (nonpassive income) for the partnerships or S corporations
                  listed above. These totals flow into Line 30.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 30 - Total Passive & Nonpassive Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On Line 30, you enter the combined income from{' '}
                  <span style={{ fontWeight: 600 }}> columns (h) + (k)</span> of
                  Line 29a. This is the total{' '}
                  <span style={{ fontWeight: 600 }}>income</span> from
                  partnerships and S corporations.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 31 - Total Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  On Line 31, you add the amounts from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    columns (g) + (i) + (j)
                  </span>{' '}
                  of Line 29b. This represents your total{' '}
                  <span style={{ fontWeight: 600 }}>
                    losses and deductions
                  </span>{' '}
                  from these entities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 32 - Total Partnership & S Corporation Income (or Loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Finally, combine{' '}
                  <span style={{ fontWeight: 600 }}> Line 30 (income)</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Line 31 (losses)</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The result is your{' '}
                  <span style={{ fontWeight: 600 }}>
                    overall net income or loss
                  </span>{' '}
                  from all partnerships and S corporations for the year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 88,
                  }}
                  id="part3"
                >
                  Part III: Income or Loss from Estates and Trusts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-estates-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-estates-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-estates-img.png"
                    loading="lazy"
                    alt="Part III: Income or Loss from Estates and Trusts"
                    width={760}
                    height={228}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 33
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (a) – Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Write the name of the estate or trust that issued the Schedule
                  K-1 to your trust.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (b) – Employer Identification Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the EIN of that estate or trust. This helps the IRS
                  match the income reported.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Passive Income and Loss Columns
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (c) – Passive deduction or loss allowed
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive losses or deductions
                  </span>{' '}
                  your trust is allowed to claim from the Schedule K-1. (If you
                  have passive activity limits → Form 8582 may be required)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (d) – Passive income from Schedule K-1
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}> passive income </span>the
                  trust received from the estate or trust—things like rental
                  income or income from activities where the trust does not
                  materially participate.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Nonpassive Income and Loss Columns
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (e) – Deduction or loss from Schedule K-1
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    nonpassive losses or deductions
                  </span>{' '}
                  (e.g., business losses where the trust materially
                  participates).
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Column (f) – Other income from Schedule K-1
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter all{' '}
                  <span style={{ fontWeight: 600 }}>nonpassive income,</span>{' '}
                  such as interest, dividends, business income, royalties, or
                  any income the estate/trust reported as “other” on the K-1.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 34a / 34b – Totals
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the amounts in each row (A & B) for passive and nonpassive
                  sections.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 35
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>
                    passive income minus passive losses
                  </span>{' '}
                  <br />
                  (Columns <span style={{ fontWeight: 600 }}>(d)</span> +{' '}
                  <span style={{ fontWeight: 600 }}>(f)</span> totals).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 36
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    passive + nonpassive losses/deductions
                  </span>{' '}
                  <br />
                  (Columns <span style={{ fontWeight: 600 }}>(c)</span> +{' '}
                  <span style={{ fontWeight: 600 }}>(e)</span> totals).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 37 – Total estate and trust income (or loss)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  Combine lines 35 and 36.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This is the final income or loss your{' '}
                  <Link
                    href="/resources/tax-exempt/form-5227-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    5227
                  </Link>{' '}
                  reports from estates and trusts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 88,
                  }}
                  id="partiv"
                >
                  Part IV - Income or Loss from Real Estate Mortgage Investment
                  Conduits (REMICs) - Residual Holder
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-residual-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-residual-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-residual-img.png"
                    loading="lazy"
                    alt="Part IV - Income or Loss from Real Estate Mortgage Investment
                  Conduits (REMICs) - Residual Holder"
                    width={760}
                    height={84}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Part IV is only used{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      if your trust owns a residual interest in a REMIC
                    </span>{' '}
                    (Real Estate Mortgage Investment Conduit).
                    <br />
                    If so, the REMIC sends you a{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule Q (Form 1066)
                    </span>{' '}
                    every quarter, showing your trust’s share of income or loss.
                  </li>
                  <li>
                    You use those numbers to fill in this section. <br />
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Do NOT attach Schedule Q
                    </span>{' '}
                    to the return - keep it in your files.
                  </li>
                  <li>
                    If you report REMIC items differently from how the REMIC
                    reported them, you may need to file Form 8082.{' '}
                  </li>
                  <li>
                    If your trust owns{' '}
                    <span style={{ fontWeight: 600 }}>
                      more than one REMIC,
                    </span>{' '}
                    you can add another sheet using the same format and then
                    combine the totals.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 38 - REMIC Residual Income
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 38 is where you total the amounts you entered in{' '}
                  <span style={{ fontWeight: 600 }}> columns (d) and (e)</span>{' '}
                  from Part IV. These amounts represent your{' '}
                  <span style={{ fontWeight: 600 }}>
                    share of the REMIC’s quarterly income or loss
                  </span>{' '}
                  from all residual interests you hold.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line only includes the{' '}
                  <span style={{ fontWeight: 600 }}>
                    regular REMIC income items,
                  </span>{' '}
                  not the minimum taxable income from column (c).
                </Typography>
                <Alert
                  severity="warning"
                  icon={<EventNoteIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b>{' '}
                  <ul>
                    <li style={{ marginBottom: 8 }}>
                      If you hold a regular interest in a REMIC (not a residual
                      interest),{' '}
                    </li>
                    <li>
                      do NOT use Schedule E for reporting that income. These
                      rules also apply to estates and trusts with REMIC residual
                      interests. Make sure Form 1041 is filled out correctly
                      when applicable.
                    </li>
                  </ul>
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 39 - Total REMIC Income or Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  On Line 39, you report the combined total from:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Column (d) (taxable income/loss), and </li>
                  <li>Column (e) (other REMIC income)</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This is the amount that carries over to:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>Schedule E, line 39</span>{' '}
                    (for individuals), or{' '}
                  </li>
                  <li>
                    The corresponding line on{' '}
                    <span style={{ fontWeight: 600 }}>Form 1041</span> (for
                    estates & trusts){' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={<WarningIcon />}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>CAUTION :</b> Do NOT include the amount from column (c) in
                  the total on Schedule E, line 39. Column (c) is reported
                  directly on Form 1040/1041 or Form 6251 instead.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                    scrollMarginTop: 88,
                  }}
                  id="sum"
                >
                  Part V: Summary
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f5227-sche-summary-img.webp" />
                  <source srcSet="/assets/images/f5227-sche-summary-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f5227-sche-summary-img.png"
                    loading="lazy"
                    alt="Schedule E (Form 1040) Part V: Summary of income and expenses, lines 40–43"
                    width={760}
                    height={216}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 40 – Net Farm Rental Income or Loss (from Form 4835)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This line asks for your{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    net income or loss from farm rentals
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If the trust rented farmland and received income, or had
                  expenses/losses, you will enter the final amount from{' '}
                  <span style={{ fontWeight: 600 }}>Form 4835</span> here.
                </Typography>
                <Box component="ul" sx={{ mb: 2 }}>
                  <li>
                    You must also complete{' '}
                    <span style={{ fontWeight: 600 }}>Line 42</span> (related to
                    farming/fishing income).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 41 – Total Income or Loss
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This line gives you the big picture of all income or losses
                  the trust reported across Schedule E.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Add the totals from{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Lines 26, 32, 37, 39, and 40{' '}
                    </span>
                  </li>
                  <li>Enter the combined amount on Line 41 </li>
                  <li>
                    Also report this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Schedule 1 (Form 1040), line 5,
                    </span>
                    if applicable{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This number shows the trust’s total net activity from
                  partnerships, S corporations, rentals, estates, trusts,
                  REMICs, and farm rentals.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 42 (For 5227 Filers) — Farming & Fishing Income Check
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Line 42 is used to report gross farming or fishing income that
                  flows to the trust. This helps determine if the trust
                  qualifies special estimated tax relief available to farming
                  and fishing activities.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Special Rule for Farming & Fishing Trusts{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A trust with significant farming or fishing income may avoid
                  estimated-tax penalties if:
                </Typography>
                <Box component="ol" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    At least two-thirds of its total income is from farming or
                    fishing, and{' '}
                  </li>
                  <li>
                    The trust’s 2025 return is filed and paid by{' '}
                    <span style={{ fontWeight: 600 }}> March 2, 2026</span>
                    .{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Paperwork Reduction Act (Simplified for 5227 Users)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    The IRS requires information on Form 5227 to properly
                    administer tax laws.
                  </li>
                  <li>
                    You only need to provide information on forms that display a
                    valid OMB control number.
                  </li>
                  <li>
                    Keep all records related to the trust if they may be needed
                    for tax purposes.{' '}
                  </li>
                  <li>
                    Completion time varies by trust and activity level. If you
                    have suggestions for simplifying Form 5227, the IRS invites
                    comments through the main return instructions.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 18, md: 17, sm: 16, xs: 16 },
                    mb: 0.5,
                  }}
                >
                  Line 43 – Reconciliation for Real Estate Professionals
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This line is only for people who qualify as real estate
                  professionals.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you meet those rules (material participation + real estate
                  - related work hours)
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the net income or loss from all rental real estate
                    activities{' '}
                  </li>
                  <li>
                    This includes amounts reported anywhere on Form 1040,
                    1040-SR, or 1040-NR{' '}
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                      scrollMarginTop: 88,
                    }}
                    id="add"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        Refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1040se.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions for Schedule E (Form 1040)
                        </Link>{' '}
                        for rental and royalty reporting, and the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i5227.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS instructions for Form 5227
                        </Link>{' '}
                        for trust reporting requirements.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Need to e-file Form 5227 with Schedule E (Form 1040)?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Keep your split-interest trust fully compliant with IRS
              requirements.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              TaxZerone helps you accurately report rental, royalty, and other
              passive income
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>{' '}
              on Schedule E and seamlessly{' '}
              <Link
                href="/tax-exempt/e-file-irs-form-5227/"
                sx={{ color: '#0202ff' }}
              >
                {' '}
                file Form 5227
              </Link>
              —quickly, securely, and with confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=5227sche`}
              >
                E-file Form 5227 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form5227ShcE;
